import * as Yup from "yup";

import { FieldConfiguration, fieldConfigurations } from "./fieldConfigurations";

// A commission is meaningless without the agent it is paid to, so each of
// these rows requires its agent row to be mapped as well.
const agentByCommissionField: Record<string, FieldConfiguration> = {
  [fieldConfigurations.STOCK_AGENT_PERCENTAGE_COMMISSION.value]:
    fieldConfigurations.STOCK_AGENT,
  [fieldConfigurations.REBATE_AGENT_PERCENTAGE_COMMISSION.value]:
    fieldConfigurations.REBATE_AGENT,
};

function validateAgentRequiredFactory(
  mappableFieldValue,
  agentConfiguration: FieldConfiguration,
) {
  return function () {
    // `this` is the Yup test context; access sibling fields via this.parent
    const agentMappableFieldValue =
      this.parent[`MappableField__${agentConfiguration.value}`];
    if (mappableFieldValue && !agentMappableFieldValue) {
      return false;
    }
    // otherwise valid
    return true;
  };
}

interface BuildValidationSchemaOptions {
  rawData: Record<string, string | number>;
  businessIdByAgentCodeLookup: Record<string, string>;
  businessIdBySaleyardCodeLookup: Record<string, string>;
}

export function buildSideLoadedValidationSchema(
  configurations: FieldConfiguration[],
  {
    rawData,
    businessIdByAgentCodeLookup,
    businessIdBySaleyardCodeLookup,
  }: BuildValidationSchemaOptions,
): Yup.AnyObjectSchema {
  const shape: Record<string, Yup.AnySchema> = {};
  configurations.forEach(configuration => {
    shape[`MappableField__${configuration.value}`] = Yup.string().nullable();
    shape[`Value__${configuration.value}`] = Yup.string().when(
      `MappableField__${configuration.value}`,
      (mappableFieldValue, schema) => {
        let resolvedSchema = schema;
        if (configuration.isBusinessLookup) {
          resolvedSchema = Yup.string().test({
            name: "matchesExistingBusiness",
            message: `No business has this Agent Code or Saleyard Code. Add one, or map ${configuration.label} to a different column.`,
            test: () => {
              const selectedValue = rawData[mappableFieldValue];
              if (!selectedValue) {
                return true;
              }
              const selectedBusinessId =
                businessIdByAgentCodeLookup[selectedValue] ||
                businessIdBySaleyardCodeLookup[selectedValue] ||
                null;
              return Boolean(selectedBusinessId);
            },
          });
        } else if (agentByCommissionField[configuration.value]) {
          const agentConfiguration =
            agentByCommissionField[configuration.value];
          resolvedSchema = Yup.string().test({
            name: "agentRequired",
            test: validateAgentRequiredFactory(
              mappableFieldValue,
              agentConfiguration,
            ),
            message: `${agentConfiguration.label} is required when ${configuration.label} is present.`,
          });
        } else if (mappableFieldValue !== null) {
          resolvedSchema = configuration.validation || Yup.string();
        }
        // Yup interpolates the field path into its built in messages, and the
        // path here is the internal `Value__x` name. A label on the outer
        // schema is discarded once `when` resolves, so it has to be applied to
        // whatever this returns.
        return resolvedSchema.label(configuration.label);
      },
    );
  });
  return Yup.object().shape(shape);
}
